/**
 * 🛡️ G2GENTE - Mobile Feedback History Page
 * Histórico de avaliações, PDIs e feedback de desempenho
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidatesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { 
  ArrowLeft,
  Loader2,
  ClipboardCheck,
  Target,
  TrendingUp,
  Star,
  ThumbsUp,
  AlertTriangle,
  MessageSquare,
  Calendar,
  Building,
  User,
  CheckCircle2,
  Clock,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function MobileFeedback() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [feedbackData, setFeedbackData] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // all, evaluations, pdis, reviews

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const response = await candidatesAPI.getFeedbackHistory();
      setFeedbackData(response.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getRecomendacaoConfig = (rec) => {
    const configs = {
      'contratar': { label: 'Recomendado', color: 'bg-green-100 text-green-700', icon: ThumbsUp },
      'revisar': { label: 'Revisar', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
      'nao_contratar': { label: 'Não Recomendado', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
    };
    return configs[rec] || { label: rec, color: 'bg-slate-100 text-slate-700', icon: ClipboardCheck };
  };

  const getStatusConfig = (status) => {
    const configs = {
      'ativo': { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700', icon: Clock },
      'concluido': { label: 'Concluído', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      'rascunho': { label: 'Rascunho', color: 'bg-slate-100 text-slate-700', icon: Clock },
      'cancelado': { label: 'Cancelado', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
    };
    return configs[status] || { label: status, color: 'bg-slate-100 text-slate-700', icon: Clock };
  };

  const getPrioridadeConfig = (prioridade) => {
    const configs = {
      'alta': { label: 'Alta', color: 'bg-red-100 text-red-700' },
      'media': { label: 'Média', color: 'bg-amber-100 text-amber-700' },
      'baixa': { label: 'Baixa', color: 'bg-green-100 text-green-700' }
    };
    return configs[prioridade] || { label: prioridade, color: 'bg-slate-100 text-slate-700' };
  };

  // Filter items based on active tab
  const getFilteredItems = () => {
    if (!feedbackData) return [];
    
    let items = [];
    
    if (activeTab === 'all' || activeTab === 'evaluations') {
      items = [...items, ...feedbackData.evaluations.map(e => ({ ...e, tipo: 'entrevista' }))];
    }
    if (activeTab === 'all' || activeTab === 'pdis') {
      items = [...items, ...feedbackData.pdis.map(p => ({ ...p, tipo: 'pdi' }))];
    }
    if (activeTab === 'all' || activeTab === 'reviews') {
      items = [...items, ...feedbackData.performance_reviews.map(r => ({ ...r, tipo: 'desempenho' }))];
    }
    
    // Sort by date
    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  const filteredItems = getFilteredItems();
  const summary = feedbackData?.summary || {};

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="mobile-feedback">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <button onClick={() => navigate('/m')} className="text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">Meu Histórico</h1>
            <p className="text-xs text-slate-500">Avaliações e feedbacks recebidos</p>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
          <Button
            variant={activeTab === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('all')}
            className={activeTab === 'all' ? 'bg-cyan-600' : ''}
          >
            Todos
          </Button>
          <Button
            variant={activeTab === 'evaluations' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('evaluations')}
            className={activeTab === 'evaluations' ? 'bg-cyan-600' : ''}
          >
            <ClipboardCheck className="w-4 h-4 mr-1" />
            Entrevistas ({summary.total_evaluations || 0})
          </Button>
          <Button
            variant={activeTab === 'pdis' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('pdis')}
            className={activeTab === 'pdis' ? 'bg-cyan-600' : ''}
          >
            <Target className="w-4 h-4 mr-1" />
            PDIs ({summary.total_pdis || 0})
          </Button>
          <Button
            variant={activeTab === 'reviews' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('reviews')}
            className={activeTab === 'reviews' ? 'bg-cyan-600' : ''}
          >
            <TrendingUp className="w-4 h-4 mr-1" />
            Desempenho ({summary.total_reviews || 0})
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {activeTab === 'all' && (
        <div className="grid grid-cols-3 gap-3 p-4">
          <Card className="border-cyan-200 bg-cyan-50">
            <CardContent className="p-3 text-center">
              <ClipboardCheck className="w-6 h-6 text-cyan-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-cyan-700">{summary.total_evaluations || 0}</p>
              <p className="text-xs text-cyan-600">Entrevistas</p>
            </CardContent>
          </Card>
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-3 text-center">
              <Target className="w-6 h-6 text-purple-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-purple-700">{summary.total_pdis || 0}</p>
              <p className="text-xs text-purple-600">PDIs</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-3 text-center">
              <TrendingUp className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-emerald-700">{summary.total_reviews || 0}</p>
              <p className="text-xs text-emerald-600">Avaliações</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content */}
      <div className="px-4">
        {filteredItems.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">Nenhum registro encontrado</h3>
            <p className="text-sm text-slate-500">
              Quando você receber avaliações ou feedbacks, eles aparecerão aqui.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredItems.map((item, idx) => (
              <Card key={`${item.tipo}-${item.id}-${idx}`} className="border-slate-200">
                <CardContent className="p-4">
                  {/* Interview Evaluation */}
                  {item.tipo === 'entrevista' && (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                            <ClipboardCheck className="w-5 h-5 text-cyan-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{item.company_name}</p>
                            <p className="text-xs text-slate-500">
                              {item.job_title || 'Avaliação de Entrevista'}
                            </p>
                          </div>
                        </div>
                        {(() => {
                          const config = getRecomendacaoConfig(item.recomendacao);
                          return (
                            <Badge className={config.color}>
                              <config.icon className="w-3 h-3 mr-1" />
                              {config.label}
                            </Badge>
                          );
                        })()}
                      </div>
                      
                      {item.media_geral && (
                        <div className="flex items-center gap-1 mb-2">
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          <span className="font-medium">{item.media_geral.toFixed(1)}</span>
                          <span className="text-slate-400">/5</span>
                        </div>
                      )}
                      
                      {item.resumo_geral && (
                        <div className="bg-slate-50 rounded-lg p-3 mb-3">
                          <p className="text-sm text-slate-600 italic">"{item.resumo_geral}"</p>
                        </div>
                      )}
                      
                      {item.pontos_fortes?.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-medium text-green-600 mb-1 flex items-center gap-1">
                            <ThumbsUp className="w-3 h-3" />
                            Pontos Fortes
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {item.pontos_fortes.map((p, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                {p}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {item.pontos_melhoria?.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-medium text-amber-600 mb-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Pontos de Melhoria
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {item.pontos_melhoria.map((p, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                {p}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-slate-400 mt-2">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {formatDate(item.created_at)}
                      </p>
                    </>
                  )}
                  
                  {/* PDI */}
                  {item.tipo === 'pdi' && (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Target className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{item.titulo}</p>
                            <p className="text-xs text-slate-500">{item.empresa}</p>
                          </div>
                        </div>
                        {(() => {
                          const config = getStatusConfig(item.status);
                          return (
                            <Badge className={config.color}>
                              <config.icon className="w-3 h-3 mr-1" />
                              {config.label}
                            </Badge>
                          );
                        })()}
                      </div>
                      
                      {/* Progress */}
                      {item.acoes_total > 0 && (
                        <div className="mb-3">
                          <div className="flex justify-between text-xs text-slate-600 mb-1">
                            <span>Progresso</span>
                            <span>{item.acoes_concluidas}/{item.acoes_total} ações</span>
                          </div>
                          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-500 rounded-full transition-all"
                              style={{ width: `${(item.acoes_concluidas / item.acoes_total) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                      
                      {item.descricao && (
                        <p className="text-sm text-slate-600 mb-3">{item.descricao}</p>
                      )}
                      
                      {/* Manager Feedback */}
                      {item.feedback_gestor && (
                        <div className="bg-purple-50 rounded-lg p-3 mb-3 border border-purple-200">
                          <p className="text-xs font-medium text-purple-700 mb-1 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Feedback do Gestor
                          </p>
                          <p className="text-sm text-purple-800 italic">"{item.feedback_gestor}"</p>
                          {item.criado_por && (
                            <p className="text-xs text-purple-600 mt-2">
                              — {item.criado_por.nome}{item.criado_por.cargo ? `, ${item.criado_por.cargo}` : ''}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {item.prioridade && (
                        <Badge className={`${getPrioridadeConfig(item.prioridade).color} mr-2`}>
                          Prioridade {getPrioridadeConfig(item.prioridade).label}
                        </Badge>
                      )}
                      
                      <p className="text-xs text-slate-400 mt-2">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {item.periodo_inicio && item.periodo_fim 
                          ? `${formatDate(item.periodo_inicio)} - ${formatDate(item.periodo_fim)}`
                          : formatDate(item.created_at)
                        }
                      </p>
                    </>
                  )}
                  
                  {/* Performance Review */}
                  {item.tipo === 'desempenho' && (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{item.ciclo}</p>
                            <p className="text-xs text-slate-500">Avaliação de Desempenho</p>
                          </div>
                        </div>
                        {item.nota_final && (
                          <div className="text-right">
                            <p className="text-2xl font-bold text-emerald-600">{item.nota_final.toFixed(1)}</p>
                            <p className="text-xs text-slate-500">Nota Final</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Manager Feedback */}
                      {item.feedback_gestor && (
                        <div className="bg-emerald-50 rounded-lg p-3 mb-3 border border-emerald-200">
                          <p className="text-xs font-medium text-emerald-700 mb-1 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            Feedback do Avaliador
                          </p>
                          <p className="text-sm text-emerald-800 italic">"{item.feedback_gestor}"</p>
                          {item.avaliador && (
                            <p className="text-xs text-emerald-600 mt-2">
                              — {item.avaliador.nome}{item.avaliador.cargo ? `, ${item.avaliador.cargo}` : ''}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {item.pontos_fortes?.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-medium text-green-600 mb-1">Pontos Fortes</p>
                          <div className="flex flex-wrap gap-1">
                            {item.pontos_fortes.map((p, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                {p}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {item.pontos_melhoria?.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-medium text-amber-600 mb-1">Pontos de Melhoria</p>
                          <div className="flex flex-wrap gap-1">
                            {item.pontos_melhoria.map((p, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                {p}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-slate-400 mt-2">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {formatDate(item.created_at)}
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
